
import { useState } from 'react';
import { DevelopmentStep } from '@/types/develop';
import { v4 as uuidv4 } from 'uuid';

export const useMobileStepEditor = (steps: DevelopmentStep[], onStepsChange: (steps: DevelopmentStep[]) => void) => {
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState<DevelopmentStep | null>(null);
  const [isNewStep, setIsNewStep] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState<number | null>(null);

  const openStepEditor = (step: DevelopmentStep) => {
    const index = steps.findIndex(s => s.id === step.id);
    setCurrentStep(step);
    setCurrentStepIndex(index);
    setIsNewStep(false);
    setIsEditorOpen(true);
  };

  const openNewStepEditor = () => {
    setCurrentStep({
      id: uuidv4(),
      name: '',
      time: 60,
      agitationType: 'intermittent',
      agitationInterval: 30,
      agitationDuration: 10,
      notes: '',
    });
    setCurrentStepIndex(null);
    setIsNewStep(true);
    setIsEditorOpen(true);
  };

  const handleSaveStep = (updatedStep: DevelopmentStep) => {
    if (isNewStep) {
      onStepsChange([...steps, updatedStep]);
    } else {
      onStepsChange(
        steps.map((step) => (step.id === updatedStep.id ? updatedStep : step))
      );
    }
    setIsEditorOpen(false);
  };

  const handleDeleteStep = () => {
    if (currentStep) {
      onStepsChange(steps.filter((step) => step.id !== currentStep.id));
    }
    setIsEditorOpen(false);
  };

  const handleMoveStepUp = (index: number) => {
    if (index <= 0) return;
    
    const newSteps = [...steps];
    const temp = newSteps[index];
    newSteps[index] = newSteps[index - 1];
    newSteps[index - 1] = temp;
    
    onStepsChange(newSteps);
  };

  const handleMoveStepDown = (index: number) => {
    if (index >= steps.length - 1) return;
    
    const newSteps = [...steps];
    const temp = newSteps[index];
    newSteps[index] = newSteps[index + 1];
    newSteps[index + 1] = temp;
    
    onStepsChange(newSteps);
  };

  // These methods are for use within the editor drawer
  const handleEditorMoveStepUp = () => {
    if (currentStepIndex === null || currentStepIndex <= 0) return;
    
    const newSteps = [...steps];
    const temp = newSteps[currentStepIndex];
    newSteps[currentStepIndex] = newSteps[currentStepIndex - 1];
    newSteps[currentStepIndex - 1] = temp;
    
    setCurrentStepIndex(currentStepIndex - 1);
    setCurrentStep(temp);
    onStepsChange(newSteps);
  };

  const handleEditorMoveStepDown = () => {
    if (currentStepIndex === null || currentStepIndex >= steps.length - 1) return;
    
    const newSteps = [...steps];
    const temp = newSteps[currentStepIndex];
    newSteps[currentStepIndex] = newSteps[currentStepIndex + 1];
    newSteps[currentStepIndex + 1] = temp;
    
    setCurrentStepIndex(currentStepIndex + 1);
    setCurrentStep(temp);
    onStepsChange(newSteps);
  };

  return {
    isEditorOpen,
    setIsEditorOpen,
    currentStep,
    currentStepIndex,
    isNewStep,
    openStepEditor,
    openNewStepEditor,
    handleSaveStep,
    handleDeleteStep,
    handleMoveStepUp,
    handleMoveStepDown,
    handleEditorMoveStepUp,
    handleEditorMoveStepDown,
  };
};
